<template>
  <div class="card mb-4">
    <div
      class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
      @click="accordionOpen = !accordionOpen"
    >
      <h5 class="mb-0"><i class="bi bi-images me-2 text-primary"></i> Repositório de Imagens</h5>
      <div class="d-flex align-items-center">
        <span v-if="totalImagens > 0" class="badge bg-success me-2">
          {{ totalImagens }} imagem(ns)
        </span>
        <i :class="['bi', accordionOpen ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
    </div>

    <div class="card-body p-0" v-show="accordionOpen">
      <!-- Abas -->
      <div class="d-flex border-bottom">
        <button
          type="button"
          class="btn btn-sm px-4 py-3 rounded-0 border-0 fw-medium"
          :class="abaAtiva === 'identidadeVisual' ? 'bg-primary-subtle text-primary' : 'bg-transparent text-body-secondary'"
          @click="abaAtiva = 'identidadeVisual'"
        >
          <i class="bi bi-palette me-1"></i> Identidade Visual
        </button>
        <button
          type="button"
          class="btn btn-sm px-4 py-3 rounded-0 border-0 fw-medium"
          :class="abaAtiva === 'imagensAvulsas' ? 'bg-primary-subtle text-primary' : 'bg-transparent text-body-secondary'"
          @click="abaAtiva = 'imagensAvulsas'"
        >
          <i class="bi bi-cloud-arrow-up me-1"></i> Imagens Avulsas
          <span v-if="imagensAvulsas.length > 0" class="badge bg-secondary ms-1">{{ imagensAvulsas.length }}</span>
        </button>
      </div>

      <!-- Conteúdo: Identidade Visual -->
      <div v-if="abaAtiva === 'identidadeVisual'">
        <div class="p-3 bg-info-subtle border-bottom">
          <p class="mb-0 small">
            <i class="bi bi-info-circle me-1"></i>
            Personalize a aparência do sistema com as imagens da sua empresa.
            Cada tipo de imagem tem dimensões específicas para garantir a melhor apresentação.
          </p>
        </div>

        <div v-if="isLoadingImagens" class="text-center py-4">
          <span class="spinner-border spinner-border-sm me-2"></span>
          Carregando configurações...
        </div>

        <div v-else-if="erroImagens" class="alert alert-warning m-3">
          {{ erroImagens }}
        </div>

        <div v-else>
          <ConfiguracaoImagemItem
            v-for="config in tiposImagemIdentidadeVisual"
            :key="config.tipoImagem"
            :config="config"
            :id-empresa="idEmpresa"
            :imagem-atual="imagensPorTipo[config.tipoImagem]"
            @imagem-atualizada="onImagemAtualizada"
            @imagem-removida="onImagemRemovida"
          />
          <div v-if="tiposImagemIdentidadeVisual.length === 0" class="text-center text-muted py-4">
            <i class="bi bi-palette fs-4 d-block mb-2 text-secondary"></i>
            <p class="mb-0">Nenhum tipo de imagem configurado para identidade visual.</p>
          </div>
        </div>
      </div>

      <!-- Conteúdo: Imagens Avulsas -->
      <div v-if="abaAtiva === 'imagensAvulsas'">
        <div class="p-3 bg-info-subtle border-bottom d-flex justify-content-between align-items-center">
          <p class="mb-0 small">
            <i class="bi bi-info-circle me-1"></i>
            Gerencie imagens de uso genérico para relatórios, dashboards e outras aplicações.
          </p>
          <button type="button" class="btn btn-sm btn-primary" @click="mostrarModalUpload = true">
            <i class="bi bi-plus-lg me-1"></i> Nova Imagem Avulsa
          </button>
        </div>

        <div v-if="isLoadingGrid" class="text-center py-4">
          <span class="spinner-border spinner-border-sm me-2"></span>
          Carregando imagens...
        </div>

        <div v-else-if="imagensAvulsas.length === 0" class="text-center text-muted py-4">
          <i class="bi bi-cloud-arrow-up fs-4 d-block mb-2 text-secondary"></i>
          <p class="mb-0">Nenhuma imagem avulsa cadastrada.</p>
          <small>Clique em "Nova Imagem Avulsa" para adicionar.</small>
        </div>

        <div v-else class="p-3">
          <div class="table-responsive">
            <table class="table table-sm table-hover align-middle mb-0">
              <thead class="table-light">
                <tr>
                  <th style="width: 80px;">Preview</th>
                  <th>Nome do Arquivo</th>
                  <th>Descrição</th>
                  <th>Dimensões</th>
                  <th>Tamanho</th>
                  <th>Data Upload</th>
                  <th class="text-end" style="width: 80px;">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="img in imagensAvulsas" :key="img.idEmpresaImagem">
                  <td>
                    <img
                      :src="img.urlImagem"
                      :alt="img.nomeArquivoOriginal"
                      class="rounded"
                      style="width: 60px; height: 60px; object-fit: cover;"
                    />
                  </td>
                  <td>
                    <span class="small">{{ img.nomeArquivoOriginal }}</span>
                  </td>
                  <td>
                    <span class="small text-muted">{{ img.descricao || '-' }}</span>
                  </td>
                  <td class="small text-muted">
                    {{ img.larguraOriginal && img.alturaOriginal ? `${img.larguraOriginal}x${img.alturaOriginal}px` : '-' }}
                  </td>
                  <td class="small text-muted">
                    {{ formatarTamanho(img.tamanhoBytes) }}
                  </td>
                  <td class="small text-muted">
                    {{ formatarDataBR(img.dataUpload) }}
                  </td>
                  <td class="text-end">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger"
                      @click="confirmarExclusao(img)"
                      title="Excluir imagem"
                    >
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Paginação -->
          <div v-if="totalPages > 1" class="d-flex justify-content-between align-items-center mt-3">
            <small class="text-muted">
              Mostrando página {{ currentPage }} de {{ totalPages }} ({{ totalCount }} registros)
            </small>
            <nav>
              <ul class="pagination pagination-sm mb-0">
                <li class="page-item" :class="{ disabled: currentPage <= 1 }">
                  <button class="page-link" @click="mudarPagina(currentPage - 1)">Anterior</button>
                </li>
                <li
                  v-for="p in pageRange"
                  :key="p"
                  class="page-item"
                  :class="{ active: p === currentPage }"
                >
                  <button class="page-link" @click="mudarPagina(p)">{{ p }}</button>
                </li>
                <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
                  <button class="page-link" @click="mudarPagina(currentPage + 1)">Próxima</button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Upload de Imagem Avulsa -->
    <UploadImagemAvulsaModal
      v-model="mostrarModalUpload"
      :id-empresa="idEmpresa"
      @imagem-uploaded="onImagemAvulsaUploaded"
      @show-notification="(p) => emit('show-notification', p)"
    />

    <!-- Confirmation Modal para exclusão -->
    <ConfirmationModal
      ref="confirmacaoExclusaoRef"
      title="Excluir Imagem"
      :message="`Deseja realmente excluir a imagem '${imagemExclusao?.nomeArquivoOriginal || ''}'?`"
      confirm-button-text="Sim, Excluir"
      cancel-button-text="Cancelar"
      @confirmed="confirmarExclusaoAcao"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import api from '@/services/api'
import ConfiguracaoImagemItem from '@/components/empresasClientes/empresas/ConfiguracaoImagemItem.vue'
import UploadImagemAvulsaModal from '@/components/empresasClientes/empresas/UploadImagemAvulsaModal.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import { formatarDataBR } from '@/utils/useDataBR'

const props = defineProps({
  idEmpresa: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['show-notification'])

const accordionOpen = ref(false)
const abaAtiva = ref('identidadeVisual')

// --- Estado: Identidade Visual ---
const tiposImagem = ref([])
const imagensEmpresa = ref([])
const isLoadingImagens = ref(false)
const erroImagens = ref(null)

const tiposImagemIdentidadeVisual = computed(() =>
  tiposImagem.value.filter(t => t.tipoImagem !== 'AVULSO')
)

const imagensPorTipo = computed(() => {
  const map = {}
  imagensEmpresa.value.forEach(img => {
    map[img.tipoImagem] = img
  })
  return map
})

const totalImagens = computed(() => imagensEmpresa.value.length)

// --- Estado: Imagens Avulsas ---
const imagensAvulsas = ref([])
const isLoadingGrid = ref(false)
const currentPage = ref(1)
const pageSize = ref(10)
const totalCount = ref(0)
const totalPages = ref(0)
const mostrarModalUpload = ref(false)
const confirmacaoExclusaoRef = ref(null)
const imagemExclusao = ref(null)

const pageRange = computed(() => {
  const range = []
  const total = totalPages.value
  const current = currentPage.value
  let start = Math.max(1, current - 2)
  let end = Math.min(total, current + 2)
  if (end - start < 4) {
    if (start === 1) end = Math.min(total, start + 4)
    else start = Math.max(1, end - 4)
  }
  for (let i = start; i <= end; i++) range.push(i)
  return range
})

// --- Métodos ---
const carregarTiposImagem = async () => {
  try {
    const response = await api.get('/EmpresaImagens/tipos')
    tiposImagem.value = response.data
  } catch (error) {
    console.error('Erro ao carregar tipos de imagem:', error)
  }
}

const carregarImagensEmpresa = async () => {
  isLoadingImagens.value = true
  try {
    const response = await api.get(`/EmpresaImagens/empresa/${props.idEmpresa}`)
    imagensEmpresa.value = response.data
  } catch (error) {
    console.error('Erro ao carregar imagens:', error)
    erroImagens.value = 'Não foi possível carregar as imagens configuradas.'
  } finally {
    isLoadingImagens.value = false
  }
}

const carregarImagensAvulsas = async (page = 1) => {
  isLoadingGrid.value = true
  currentPage.value = page
  try {
    const response = await api.get(`/EmpresaImagens/empresa/${props.idEmpresa}/grid`, {
      params: {
        pageNumber: page,
        pageSize: pageSize.value,
        tipoImagem: 'AVULSO'
      }
    })
    imagensAvulsas.value = response.data

    const paginationHeader = response.headers['x-pagination']
    if (paginationHeader) {
      try {
        const pag = JSON.parse(paginationHeader)
        totalCount.value = pag.TotalCount || 0
        totalPages.value = pag.TotalPages || 0
      } catch (e) {
        console.warn('Erro ao parsear header de paginação:', e)
      }
    }
  } catch (error) {
    console.error('Erro ao carregar grid de imagens avulsas:', error)
    emit('show-notification', { message: 'Erro ao carregar imagens avulsas.', type: 'error' })
  } finally {
    isLoadingGrid.value = false
  }
}

const mudarPagina = (page) => {
  if (page < 1 || page > totalPages.value) return
  carregarImagensAvulsas(page)
}

const onImagemAtualizada = (imagem) => {
  const index = imagensEmpresa.value.findIndex(i => i.tipoImagem === imagem.tipoImagem)
  if (index >= 0) {
    imagensEmpresa.value[index] = imagem
  } else {
    imagensEmpresa.value.push(imagem)
  }
  emit('show-notification', { message: 'Imagem atualizada com sucesso!', type: 'success' })
}

const onImagemRemovida = (tipoImagem) => {
  const index = imagensEmpresa.value.findIndex(i => i.tipoImagem === tipoImagem)
  if (index >= 0) {
    imagensEmpresa.value.splice(index, 1)
  }
  emit('show-notification', { message: 'Imagem removida com sucesso!', type: 'success' })
}

const onImagemAvulsaUploaded = () => {
  mostrarModalUpload.value = false
  carregarImagensAvulsas(1)
  emit('show-notification', { message: 'Imagem avulsa enviada com sucesso!', type: 'success' })
}

const confirmarExclusao = (img) => {
  imagemExclusao.value = img
  confirmacaoExclusaoRef.value?.show()
}

const confirmarExclusaoAcao = async (confirmed) => {
  if (!confirmed || !imagemExclusao.value) return
  const img = imagemExclusao.value
  imagemExclusao.value = null

  try {
    await api.delete(`/EmpresaImagens/${img.idEmpresaImagem}`)
    carregarImagensAvulsas(currentPage.value)
    emit('show-notification', { message: 'Imagem excluída com sucesso!', type: 'success' })
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao excluir imagem.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const formatarTamanho = (bytes) => {
  if (!bytes) return '-'
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

// Recarregar quando a aba mudar para avulsas
watch(abaAtiva, (nova) => {
  if (nova === 'imagensAvulsas' && imagensAvulsas.value.length === 0 && !isLoadingGrid.value) {
    carregarImagensAvulsas(1)
  }
})

onMounted(() => {
  carregarTiposImagem()
  carregarImagensEmpresa()
})
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
